import { caseFeature } from './case.reducer'
import { createSelector } from '@ngrx/store'
import {
    PropertySummary,
    SegObjectPropertyMap,
    MarkerDimension,
    clusteringResultMatchesLoadedMaps,
    dedupeClusteringResultsByName,
} from './case.state'
import {
    filterNamesByMatchedClusteringResults,
    resolvePropertySummaries,
} from '../../shared/utils/property-catalog-utils'
import { selectLoadedSegmentationSources } from '../viewer/viewer.selectors'

export const {
    selectCaseId,
    selectCaseName,
    selectSharedPropertyNames: selectSharedPropertyNamesFromState,
    selectCaseSegObjectPropertyMap,
    selectCaseSegObjectPropertyMaps,
    selectCaseRcnResults,
    selectCaseNeighborhoodEnrichmentResults,
    selectCaseCellTypeDetectionResults,
    selectCaseTsneResults,
    selectCaseUmapResults,
    selectCasePlottingResults,
    selectIsSharedCase,
} = caseFeature

export const selectHasCaseId = createSelector(
    selectCaseId,
    (caseId: number | null): boolean => caseId !== null
)

export const selectPropertyCatalogByMapId = createSelector(
    caseFeature.selectCaseState,
    (state): Record<number, PropertySummary[]> => state.propertyCatalogByMapId
)

export const selectHasMarkers = createSelector(
    selectSharedPropertyNamesFromState,
    (names: string[]): boolean => names.length > 0
)

export const selectHasSegObjectPropertyMap = createSelector(
    selectCaseSegObjectPropertyMap,
    (segobjectpropertymap: SegObjectPropertyMap | undefined): boolean =>
        segobjectpropertymap !== undefined
)

export const selectCaseSegObjectPropertyMapsForLoadedSegLayers = createSelector(
    selectCaseSegObjectPropertyMaps,
    selectLoadedSegmentationSources,
    (maps, loadedSegSources): SegObjectPropertyMap[] => {
        if (loadedSegSources.length === 0) return []
        const sources = new Set(loadedSegSources)
        return maps
            .filter(map => !!map.segSource && sources.has(map.segSource))
            .slice()
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }))
    }
)

export const selectHasSegObjectPropertyMaps = createSelector(
    selectCaseSegObjectPropertyMapsForLoadedSegLayers,
    (segobjectpropertymaps: SegObjectPropertyMap[]): boolean => segobjectpropertymaps.length > 0
)

/** Right panel stays open whenever intensity maps exist; selection state must not auto-close it. */
export const selectSelectionPanelVisible = selectHasSegObjectPropertyMaps

export const selectLoadedSegObjectPropertyMaps = createSelector(
    caseFeature.selectCaseState,
    (state): SegObjectPropertyMap[] => state.loadedSegObjectPropertyMaps
)

export const selectLoadedSegSources = createSelector(
    selectLoadedSegObjectPropertyMaps,
    (maps): string[] => [
        ...new Set(maps.map(m => m.segSource).filter((s): s is string => !!s)),
    ]
)

export const selectMarkerDimensionsByMapId = createSelector(
    caseFeature.selectCaseState,
    (state): Record<number, MarkerDimension[]> => state.markerDimensionsByMapId
)

/**
 * Shared property names for plot axes: hide TSNE/UMAP properties unless their result
 * exact-matches loaded maps.
 */
export const selectSharedPropertyNames = createSelector(
    selectSharedPropertyNamesFromState,
    selectCaseTsneResults,
    selectCaseUmapResults,
    selectLoadedSegObjectPropertyMaps,
    (names, tsne, umap, loadedMaps): string[] =>
        filterNamesByMatchedClusteringResults(names, tsne, umap, loadedMaps)
)

export const selectSharedPropertyNameSet = createSelector(
    selectSharedPropertyNames,
    (names): Set<string> => new Set(names)
)

/**
 * Full shared PropertySummary[] for job UIs (clustering marker pickers).
 * Prefer selectSharedPropertyNames + lazy filter for plot dropdowns.
 */
export const selectCaseMarkers = createSelector(
    selectSharedPropertyNamesFromState,
    selectPropertyCatalogByMapId,
    selectLoadedSegObjectPropertyMaps,
    (names, byMapId, loadedMaps): PropertySummary[] =>
        resolvePropertySummaries(names, byMapId, loadedMaps)
)

export const selectMatchedTsneResults = createSelector(
    selectCaseTsneResults,
    selectLoadedSegObjectPropertyMaps,
    (tsne, loadedMaps) =>
        dedupeClusteringResultsByName(
            tsne.filter(result => clusteringResultMatchesLoadedMaps(result, loadedMaps))
        )
)

export const selectMatchedUmapResults = createSelector(
    selectCaseUmapResults,
    selectLoadedSegObjectPropertyMaps,
    (umap, loadedMaps) =>
        dedupeClusteringResultsByName(
            umap.filter(result => clusteringResultMatchesLoadedMaps(result, loadedMaps))
        )
)
